import React, { useCallback, useMemo, useRef } from 'react';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import { useModalActions } from '../../Modals/modalActions';
import { MetadataFormField } from '@cube3/common/model/schema/resources/metadata-category';
import { getFieldsFromSchema } from '../utils/makeFieldsFromSchema';
import { GenericForm } from '@cube3/forms/src/forms/genericForm/GenericForm';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { makeSchema } from '../utils/makeSchema';
import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';
import { getFieldValue } from '@cube3/forms/src/utils/getFieldValue';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { MetadataValueTypes } from '@cube3/common/model/schema/resources/metadata-field-type';
import { useFieldTypeSchemas } from '../hooks/useFieldTypeSchemas';

interface Props {
  modalContext: {
    field: MetadataFormField;
    onChange: (field) => void;
  };
}

export const FieldAdvancedSettingsModal = (props: Props) => {
  const { field, onChange } = props.modalContext;
  const { previousModal } = useModalActions();

  const initialFields = useRef(getFieldsFromSchema(field)).current;

  const { fieldTypeSchemas } = useFieldTypeSchemas()

  const { fields: fieldsWithState, values } = useGenericFormState({
    fields: initialFields,
    initialValues: useMemo(
      () =>
        initialFields.map((f) => ({
          id: f.id,
          value: f['value']
        })),
      []
    )
  });

  const dirty = initialFields.find(
    (init) => init['value'] !== getFieldValue(values, init.id)?.value
  );

  const handleSave = useCallback(() => {
    if (dirty) {
      const schema = makeSchema(values, field.value_type, fieldTypeSchemas);

      const updated = {
        ...field,
        id: field.id?.replace(/(key|field)_/, ''),
        temporaryId: field['temporaryId']?.replace(/(key|field)_/, ''),
        required: getFieldValue(values, 'required')?.value,
        json_schema: JSON.stringify(schema)
      };
      onChange(updated);
    }
    previousModal();
  }, [onChange, dirty, field, values]);

  return (
    <ModalMenuUI
      title={`Edit advanced settings: ${field.display_name}`}
      onCloseEvent={previousModal}
      footerLeftComponent={<Button label="Cancel" onClick={previousModal} />}
      footerRightComponent={
        <Button
          label={dirty ? 'Save' : 'Done'}
          onClick={handleSave}
          background="primary"
          buttonStyle="solid"
        />
      }
    >
      <GenericForm fields={fieldsWithState} textAlign="left" labelWidth={32} />
    </ModalMenuUI>
  );
};
